import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CartContext from '../context/CartContext';
import AuthContext from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { orderAPI, featureFlagAPI } from '../utils/api';
import { lineUnitPrice, lineVariantLabel } from '../utils/cartLine';

const CheckoutPage = () => {
    const { cart, clearCart, isGuest } = useContext(CartContext);
    const { user, isAuthenticated } = useContext(AuthContext);
    const { convertPrice, getCurrencySymbol } = useCurrency();
    const navigate = useNavigate();
    const [paymentsEnabled, setPaymentsEnabled] = useState(true);
    const [checkingFlag, setCheckingFlag] = useState(true);
    
    const [formData, setFormData] = useState({
        email: user?.email || '',
        firstName: user?.firstName || '',
        phone: user?.phone || '',
        street: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        subscribeToUpdates: false
    });
    const [loading, setLoading] = useState(false);

    // Check if payments feature is enabled
    useEffect(() => {
        const checkPaymentsFlag = async () => {
            try {
                const response = await featureFlagAPI.isEnabled('payments');
                setPaymentsEnabled(response.data.enabled);
            } catch (err) {
                console.error('Error checking payments flag:', err);
                // Default to true if check fails
                setPaymentsEnabled(true);
            } finally {
                setCheckingFlag(false);
            }
        };

        checkPaymentsFlag();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // For guest users, create order with guest info
            const orderData = {
                shippingAddress: {
                    street: formData.street,
                    city: formData.city,
                    state: formData.state,
                    zip: formData.zip,
                    country: formData.country
                }
            };

            // Payment is arranged over WhatsApp, so this number is required for
            // every order, not just guest ones.
            orderData.contactPhone = formData.phone;

            // Add guest contact info if not authenticated
            if (isGuest || !isAuthenticated) {
                orderData.guestEmail = formData.email;
                orderData.guestPhone = formData.phone;
                orderData.guestName = formData.firstName;
                orderData.subscribeToUpdates = formData.subscribeToUpdates;

                // For guest checkout, include items from local cart
                orderData.items = cart.items.map(item => ({
                    productId: item.productId?._id || item.productId,
                    quantity: item.quantity,
                    price: lineUnitPrice(item),
                    variantId: item.variantId || null,
                    variantLabel: lineVariantLabel(item) || undefined
                }));
            }

            const { data: order } = await orderAPI.create(orderData);

            clearCart();
            // Straight to payment instructions — the order is not paid yet, so
            // dropping the customer on the home page would strand them.
            navigate(`/order/${order.publicToken}`);
        } catch (err) {
            alert('Failed to place order: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    if (!cart || !cart.items || cart.items.length === 0) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-light mb-8 tracking-tight">Your cart is empty</h2>
                    <button onClick={() => navigate('/catalog')} className="bg-black text-white px-10 py-4 hover:bg-gray-900 transition-all duration-300 font-light tracking-wide">
                        Continue Shopping
                    </button>
                </div>
            </div>
        );
    }

    // Check if payments feature is disabled
    if (!checkingFlag && !paymentsEnabled) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center max-w-md">
                    <h2 className="text-2xl font-light mb-4 tracking-tight">Checkout Unavailable</h2>
                    <p className="text-gray-600 font-light mb-8">Payment processing is currently unavailable. Please try again later.</p>
                    <button onClick={() => navigate('/catalog')} className="bg-black text-white px-10 py-4 hover:bg-gray-900 transition-all duration-300 font-light tracking-wide">
                        Back to Store
                    </button>
                </div>
            </div>
        );
    }

    const total = cart?.items?.reduce((sum, item) => {
        const price = lineUnitPrice(item);
        return sum + price * item.quantity;
    }, 0) || 0;

    return (
        <>
            {/* Slim header. The site nav is hidden during checkout, so this is
                the only way back — without it the customer is stranded. */}
            <header className="border-b border-gray-200 bg-white sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate('/cart')}
                        className="text-sm font-light text-gray-600 hover:text-black transition-colors flex items-center gap-2"
                    >
                        ← Back to cart
                    </button>
                    <span className="text-lg font-light tracking-tight text-black">Doctape</span>
                    <span className="text-xs font-light text-gray-400 hidden sm:flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Secure checkout
                    </span>
                </div>
            </header>

            {/* One responsive layout for every screen size. There used to be a
                separate mobile tree that opened the form in a drawer; it drifted
                out of sync and silently skipped the payment instructions. */}
            <div className="min-h-screen bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-black mb-8 lg:mb-12 tracking-tight">Checkout</h1>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                        <div className="lg:col-span-2">
                            {isGuest || !isAuthenticated ? (
                                <div className="mb-8 p-6 bg-black text-white border border-white">
                                    <p className="text-sm font-light tracking-wide">
                                        ✓ Checking out as a guest. Enter your email to receive order confirmation.
                                    </p>
                                </div>
                            ) : null}

                            <h2 className="text-2xl lg:text-3xl font-light text-black mb-6 lg:mb-8 tracking-tight">Contact Information</h2>
                            <div className="space-y-6 mb-12">
                                <div>
                                    <label className="block text-sm font-light text-black mb-3 tracking-wide">Email Address *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        disabled={isAuthenticated && !isGuest}
                                        className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black disabled:bg-gray-100 font-light"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-light text-black mb-3 tracking-wide">Full Name *</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            required
                                            disabled={isAuthenticated && !isGuest}
                                            className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black disabled:bg-gray-100 font-light"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-light text-black mb-3 tracking-wide">WhatsApp Number *</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                            placeholder="012-345 6789"
                                            className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black font-light"
                                        />
                                        <p className="text-xs text-gray-500 font-light mt-2">
                                            Payment instructions and tracking updates are sent here.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <h2 className="text-2xl lg:text-3xl font-light text-black mb-6 lg:mb-8 tracking-tight">Shipping Address</h2>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-light text-black mb-3 tracking-wide">Street Address *</label>
                                    <input
                                        type="text"
                                        name="street"
                                        value={formData.street}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black font-light"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-light text-black mb-3 tracking-wide">City *</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black font-light"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-light text-black mb-3 tracking-wide">State/Province *</label>
                                        <input
                                            type="text"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black font-light"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-light text-black mb-3 tracking-wide">ZIP/Postal Code *</label>
                                        <input
                                            type="text"
                                            name="zip"
                                            value={formData.zip}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black font-light"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-light text-black mb-3 tracking-wide">Country *</label>
                                        <input
                                            type="text"
                                            name="country"
                                            value={formData.country}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-3 border border-gray-300 text-black focus:outline-none focus:border-black font-light"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-6 bg-white border border-gray-200 mt-8">
                                    <input
                                        type="checkbox"
                                        id="subscribeToUpdates"
                                        name="subscribeToUpdates"
                                        checked={formData.subscribeToUpdates}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-black bg-white border-gray-300 cursor-pointer mt-1"
                                    />
                                    <label htmlFor="subscribeToUpdates" className="text-sm text-gray-700 cursor-pointer font-light">
                                        I'd like to receive updates about new products and offers
                                    </label>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-black text-white py-4 hover:bg-gray-900 transition-all duration-300 disabled:opacity-50 mt-8 font-light tracking-wide"
                                >
                                    {loading ? 'Processing...' : 'Place Order'}
                                </button>
                            </form>
                        </div>

                        <div className="bg-white border border-black p-6 lg:p-8 h-fit lg:sticky lg:top-24">
                            <h2 className="text-xl lg:text-2xl font-light text-black mb-6 lg:mb-8 tracking-tight">Order Summary</h2>
                            <div className="space-y-3 border-b border-gray-200 pb-6 mb-6 max-h-72 overflow-y-auto">
                                {cart?.items?.map(item => (
                                    <div key={item.productId?._id || item._id} className="flex justify-between text-sm text-gray-700 font-light">
                                        <span className="flex-1">{item.productId?.name || 'Product'} × {item.quantity}</span>
                                        <span>{getCurrencySymbol()}{convertPrice(lineUnitPrice(item) * item.quantity)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-3 border-b border-gray-200 pb-6 mb-6">
                                <div className="flex justify-between text-sm text-gray-700 font-light">
                                    <span>Subtotal</span>
                                    <span>{getCurrencySymbol()}{convertPrice(total)}</span>
                                </div>
                                <div className="flex justify-between text-sm text-gray-700 font-light">
                                    <span>Shipping</span>
                                    <span>Free</span>
                                </div>
                            </div>
                            <div className="flex justify-between text-lg font-light text-black">
                                <span>Total</span>
                                <span className="text-xl">{getCurrencySymbol()}{convertPrice(total)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};

export default CheckoutPage;
